/**
 * Export Utilities for Strategy Performance Data
 * Supports: CSV, XLS, JSON, PDF formats
 * AI-ready with structured data
 */

import * as XLSX from 'xlsx';

/**
 * Convert data to CSV format
 */
export const convertToCSV = (data: any[], filename: string): string => {
  if (!data.length) return '';
  
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values that might contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ];
  
  return csvRows.join('\n');
};

/**
 * Download data as CSV file
 */
export const downloadCSV = (data: any[], filename: string) => {
  const csv = convertToCSV(data, filename);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
};

/**
 * Download data as XLS file
 */
export const downloadXLS = (data: any[], filename: string, sheetName: string = 'Data') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

/**
 * Download data as JSON file (AI-optimized)
 */
export const downloadJSON = (data: any, filename: string, pretty: boolean = true) => {
  const json = pretty ? JSON.stringify(data, null, 2) : JSON.stringify(data);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
  downloadBlob(blob, `${filename}.json`);
};

/**
 * Download data as PDF using browser print
 */
export const downloadPDF = (content: string, filename: string) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${filename}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        table { border-collapse: collapse; width: 100%; margin: 20px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #4a5568; color: white; }
        tr:nth-child(even) { background-color: #f2f2f2; }
        h1, h2, h3 { color: #1a202c; }
        .metric { display: inline-block; margin: 10px 20px; }
        .header { background: #4a5568; color: white; padding: 20px; margin-bottom: 20px; }
      </style>
    </head>
    <body>
      ${content}
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.close();
          }, 500);
        };
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

/**
 * Generic blob download
 */
const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Format data for export - AI optimization
 */
export const formatForAI = (data: any): string => {
  return JSON.stringify(data, null, 2);
};

/**
 * Export all performance data in multiple formats
 */
export const exportPerformanceData = {
  /**
   * Export monthly returns with all details
   */
  monthlyReturns: (data: any[], metrics: any) => {
    return {
      csv: () => downloadCSV(data, 'monthly_returns'),
      xls: () => downloadXLS(data, 'monthly_returns', 'Monthly Returns'),
      json: () => downloadJSON({ monthly_returns: data, summary: metrics }, 'monthly_returns')
    };
  },

  /**
   * Export equity curve data
   */
  equityCurve: (data: any[]) => {
    return {
      csv: () => downloadCSV(data, 'equity_curve'),
      xls: () => downloadXLS(data, 'equity_curve', 'Equity Curve'),
      json: () => downloadJSON({ equity_curve: data }, 'equity_curve')
    };
  },

  /**
   * Export drawdown analysis
   */
  drawdown: (data: any[], stats: any) => {
    return {
      csv: () => downloadCSV(data, 'drawdown'),
      xls: () => downloadXLS(data, 'drawdown', 'Drawdown'),
      json: () => downloadJSON({ drawdown: data, statistics: stats }, 'drawdown')
    };
  },

  /**
   * Export full performance report
   */
  fullReport: (performanceData: any) => {
    const report = {
      metadata: {
        strategy_id: performanceData.strategy_id,
        name: performanceData.name,
        exported_at: new Date().toISOString(),
        version: '1.0'
      },
      summary: performanceData.metrics,
      equity_curve: performanceData.equity_curve,
      drawdown: performanceData.drawdown,
      monthly_returns: performanceData.monthly_returns,
      benchmark_comparison: performanceData.benchmark_comparison
    };
    
    return {
      csv: () => {
        // Export all data as separate CSV files in a zip would be ideal
        // For now, export the monthly returns as primary
        downloadCSV(performanceData.monthly_returns, 'full_report_monthly');
      },
      xls: () => {
        const workbook = XLSX.utils.book_new();
        
        // Summary sheet
        const summaryData = Object.entries(performanceData.metrics).map(([key, value]) => ({ metric: key, value }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'Summary');
        
        // Equity curve
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(performanceData.equity_curve), 'Equity Curve');
        
        // Drawdown
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(performanceData.drawdown), 'Drawdown');
        
        // Monthly returns
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(performanceData.monthly_returns), 'Monthly Returns');
        
        XLSX.writeFile(workbook, 'full_performance_report.xlsx');
      },
      json: () => downloadJSON(report, 'full_performance_report')
    };
  }
};

/**
 * Generate PDF-ready HTML for performance report
 */
export const generatePDFContent = (data: any): string => {
  return `
    <div class="header">
      <h1>${data.name}</h1>
      <p>Strategy Performance Report</p>
      <p>Generated: ${new Date().toLocaleDateString()}</p>
    </div>
    
    <h2>Performance Metrics</h2>
    <div>
      ${Object.entries(data.metrics).map(([key, value]) => `
        <div class="metric">
          <strong>${key.replace(/_/g, ' ').toUpperCase()}:</strong> ${value}
        </div>
      `).join('')}
    </div>
    
    <h2>Monthly Returns</h2>
    <table>
      <tr>
        <th>Month</th>
        <th>Trades</th>
        <th>P&L</th>
        <th>Return %</th>
        <th>Win Rate</th>
      </tr>
      ${data.monthly_returns.map((row: any) => `
        <tr>
          <td>${row.month}</td>
          <td>${row.trades}</td>
          <td>₹${row.pnl?.toLocaleString()}</td>
          <td>${row.return_pct}%</td>
          <td>${row.win_rate}%</td>
        </tr>
      `).join('')}
    </table>
  `;
};

export default {
  convertToCSV,
  downloadCSV,
  downloadXLS,
  downloadJSON,
  downloadPDF,
  formatForAI,
  exportPerformanceData,
  generatePDFContent
};
